import { useState, useMemo, type ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useExamSections, useExamStats, useLeaderboard, useQuestionsFeed, useGeneralExams, useActiveSessions, useCreateSession, useDeleteSession, useCompleteSession, useSessionHistory, useFriends } from '../lib/api';
import type { ExamSectionStat, StudySession } from '../lib/api';
import { deriveCategories, buildCategoryIndex } from '../lib/topic-tags';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { getFlaggedQuestions } from '../lib/flagged-questions';
import { motion, PageTransition, FadeIn, Stagger, Pressable, Modal, CountUp, Skeleton, Confetti } from '../lib/motion';

export function ExamDashboardPage() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { data: examsData } = useGeneralExams();
  const { data: sectionsData } = useExamSections(examId ?? null);
  const { data: statsData } = useExamStats(examId ?? null);
  const { data: lb } = useLeaderboard('exam', examId ?? null);
  const { data: sessionsData } = useActiveSessions({ examId: examId ?? null });
  const createSession = useCreateSession();
  const deleteSession = useDeleteSession();
  const completeSession = useCompleteSession();
  const { data: historyData } = useSessionHistory({ examId: examId ?? null });

  const exam = examsData?.exams?.find(e => e.id === examId);
  const sections = sectionsData?.sections ?? [];
  const stats = statsData?.stats;
  const activeSessions = sessionsData?.sessions ?? [];

  const scienceSections = sections.filter(s => s.code !== 'cars');
  const verbalSections = sections.filter(s => s.code === 'cars');
  const hasScienceVerbalSplit = verbalSections.length > 0 && scienceSections.length > 0;

  // Topic filter state
  const [showTopicPicker, setShowTopicPicker] = useState(false);
  const [studyTarget, setStudyTarget] = useState<'science' | 'verbal' | 'section'>('science');
  const [targetSectionId, setTargetSectionId] = useState<string | null>(null);
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [questionFilter, setQuestionFilter] = useState<'all' | 'mine' | 'friends'>('all');
  const [studyMode, setStudyMode] = useState<'all' | 'passages' | 'discrete'>('all');
  const [sessionCount, setSessionCount] = useState(20);
  const [sessionName, setSessionName] = useState('');
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [answeredWrongOnly, setAnsweredWrongOnly] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const feedSectionId = targetSectionId ?? (studyTarget === 'verbal' ? verbalSections[0]?.id : null);
  const feedParams = feedSectionId
    ? { exam_section_id: feedSectionId }
    : (studyTarget === 'science' && examId ? { exam_id: examId } : {});
  const { data: feedData, isLoading: feedLoading } = useQuestionsFeed(feedParams);

  const allStudyableQuestions = useMemo(
    () => (feedData?.questions ?? []).filter((q: any) => q.type !== 'flashcard' && q.type !== 'quick_mcq'),
    [feedData?.questions],
  );
  const { data: friendsData } = useFriends();
  const friendIds = useMemo(
    () => new Set((friendsData?.friends ?? []).filter(f => f.status === 'accepted').map(f => f.id)),
    [friendsData],
  );
  const studyableQuestions = useMemo(() => {
    let qs = allStudyableQuestions;
    if (questionFilter === 'mine' && feedData?.currentUserId) {
      qs = qs.filter((q: any) => q.authorUserId === feedData.currentUserId);
    } else if (questionFilter === 'friends') {
      qs = qs.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId));
    }
    if (studyMode === 'passages') {
      qs = qs.filter((q: any) => q.payload?.passage_type === 'passage-based' || q.payload?.passage);
    } else if (studyMode === 'discrete') {
      qs = qs.filter((q: any) => q.payload?.passage_type !== 'passage-based' && !q.payload?.passage);
    }
    return qs;
  }, [allStudyableQuestions, questionFilter, feedData?.currentUserId, friendIds, studyMode]);
  const categoryIndex = useMemo(() => buildCategoryIndex(studyableQuestions), [studyableQuestions]);
  const availableTopics = useMemo(() => {
    return Array.from(categoryIndex.entries())
      .filter(([name]) => !/^mcat$/i.test(name))
      .sort((a, b) => b[1].size - a[1].size)
      .map(([name, qIds]) => ({ name, count: qIds.size }));
  }, [categoryIndex]);

  const allSelected = selectedTopics.size === 0;
  const examDefaultLabel = useMemo(() => {
    const topicNames = allSelected ? [] : Array.from(selectedTopics);
    return allSelected
      ? 'All Topics'
      : topicNames.slice(0, 3).join(', ') + (topicNames.length > 3 ? ` +${topicNames.length - 3}` : '');
  }, [allSelected, selectedTopics]);
  const answeredSet = useMemo(
    () => new Set(feedData?.answeredQuestionIds ?? []),
    [feedData?.answeredQuestionIds],
  );
  const wrongSet = useMemo(
    () => new Set(feedData?.wrongQuestionIds ?? []),
    [feedData?.wrongQuestionIds],
  );

  const applyFilters = (pool: typeof studyableQuestions) => {
    if (unansweredOnly) pool = pool.filter(q => !answeredSet.has(q.id));
    if (answeredWrongOnly) pool = pool.filter(q => wrongSet.has(q.id));
    return pool;
  };

  const selectedQuestionCount = useMemo(() => {
    let pool = studyableQuestions;
    if (!allSelected) {
      const ids = new Set<string>();
      for (const cat of selectedTopics) {
        const qIds = categoryIndex.get(cat);
        if (qIds) qIds.forEach(id => ids.add(id));
      }
      pool = pool.filter(q => ids.has(q.id));
    }
    pool = applyFilters(pool);
    return pool.length;
  }, [allSelected, selectedTopics, categoryIndex, studyableQuestions, unansweredOnly, answeredWrongOnly, answeredSet, wrongSet]);

  const filteredTopicCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const [name, qIds] of categoryIndex.entries()) {
      const topicQuestions = studyableQuestions.filter(q => qIds.has(q.id));
      counts.set(name, applyFilters(topicQuestions).length);
    }
    return counts;
  }, [categoryIndex, studyableQuestions, unansweredOnly, answeredWrongOnly, answeredSet, wrongSet]);

  const getFilteredQuestionIds = (): string[] => {
    let qs = studyableQuestions;
    if (!allSelected) {
      const ids = new Set<string>();
      for (const cat of selectedTopics) {
        const qIds = categoryIndex.get(cat);
        if (qIds) qIds.forEach(id => ids.add(id));
      }
      qs = qs.filter(q => ids.has(q.id));
    }
    qs = applyFilters(qs);
    if (studyMode === 'passages') {
      const grouped = new Map<string, typeof qs>();
      const noPassage: typeof qs = [];
      for (const q of qs) {
        const passageKey = (q.payload as any)?.passage?.slice(0, 100);
        if (passageKey) {
          const group = grouped.get(passageKey) ?? [];
          group.push(q);
          grouped.set(passageKey, group);
        } else {
          noPassage.push(q);
        }
      }
      return [...Array.from(grouped.values()).flat(), ...noPassage].map(q => q.id);
    }
    return qs.map(q => q.id);
  };

  const createAndNavigate = async (sectionId: string) => {
    let questionIds = getFilteredQuestionIds();
    if (questionIds.length === 0) return;

    // Cap to the user's chosen count. Passage mode keeps whole passage groups
    // intact (the ids are already grouped), so we don't slice there.
    if (studyMode !== 'passages') {
      for (let i = questionIds.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [questionIds[i], questionIds[j]] = [questionIds[j]!, questionIds[i]!];
      }
      questionIds = questionIds.slice(0, Math.min(Math.max(1, sessionCount), questionIds.length));
    }

    const topicNames = allSelected ? [] : Array.from(selectedTopics);
    const base = sessionName.trim() || examDefaultLabel;
    const taken = new Set(activeSessions.map(s => s.label).filter(Boolean));
    let label = base;
    if (taken.has(label)) { let n = 2; while (taken.has(`${base} (${n})`)) n++; label = `${base} (${n})`; }

    const result = await createSession.mutateAsync({
      examSectionId: sectionId,
      filters: allSelected ? null : { topics: topicNames },
      questionIds,
      label,
    });

    navigate(`/study/exam/${sectionId}?session=${result.session.id}`);
  };

  // Don't open the topic picker until the question feed has loaded, otherwise it
  // opens with no topics/questions (and confirm is disabled) on a freshly-opened
  // section until the user backs out and retries.
  const handleStartScience = () => {
    if (feedLoading) return;
    setStudyTarget('science');
    setTargetSectionId(null);
    setSelectedTopics(new Set());
    setSessionName('');
    setShowTopicPicker(true);
  };

  const handleStartVerbal = () => {
    if (feedLoading) return;
    setStudyTarget('verbal');
    setTargetSectionId(verbalSections.length === 1 ? verbalSections[0]!.id : null);
    setSelectedTopics(new Set());
    setSessionName('');
    setShowTopicPicker(true);
  };

  const handleStartSection = (sectionId: string) => {
    if (feedLoading) return;
    setStudyTarget('section');
    setTargetSectionId(sectionId);
    setSelectedTopics(new Set());
    setSessionName('');
    setShowTopicPicker(true);
  };

  const confirmTopics = () => {
    setShowTopicPicker(false);
    const sid = targetSectionId
      ?? (studyTarget === 'verbal' ? verbalSections[0]?.id : scienceSections[0]?.id);
    if (sid) createAndNavigate(sid);
  };

  const toggleTopic = (topic: string) => {
    setSelectedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topic)) next.delete(topic);
      else next.add(topic);
      return next;
    });
  };

  const resumeSession = (session: StudySession) => {
    const sectionId = session.examSectionId ?? session.classSectionId;
    if (!sectionId) return;
    const contextType = session.examSectionId ? 'exam' : 'class';
    navigate(`/study/${contextType}/${sectionId}?session=${session.id}`);
  };

  if (!exam) {
    return (
      <div className="max-w-lg mx-auto px-4 pb-24 pt-2 space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-36 rounded-3xl" />
        <Skeleton className="h-14 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
      </div>
    );
  }

  const previewSessions = activeSessions.slice(0, 2);
  const previewSections = stats?.sections.slice(0, 2) ?? [];
  const previewLeaderboard = lb?.entries.slice(0, 3) ?? [];

  const attempted = stats?.totals.attempted ?? 0;
  const totalQ = stats?.totals.total ?? 0;
  const accuracy = stats?.totals.accuracy ?? 0;
  const coverage = totalQ > 0 ? Math.round((attempted / totalQ) * 100) : 0;

  // Motivating one-liner for the cockpit. Purely presentational.
  const heroLine =
    attempted === 0 ? 'Fresh start. Your first session sets the pace.'
    : coverage >= 100 ? 'Full coverage. You have seen every question in the bank.'
    : accuracy >= 80 ? 'Elite accuracy. Keep that edge sharp.'
    : accuracy >= 70 ? 'Strong accuracy. Momentum looks good.'
    : accuracy >= 50 ? 'Solid base. Push past 70% and it sticks.'
    : 'Reps build recall. One more session today.';

  return (
    <PageTransition className="max-w-lg mx-auto px-4 pb-24 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-1 pt-1">
        <Link
          to="/exams"
          aria-label="Back to all exams"
          className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full text-squishy-primary hover:bg-teal-50 transition-colors shrink-0"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
        </Link>
        <div className="min-w-0">
          <h1 className="font-display text-xl font-semibold text-squishy-dark tracking-tight leading-tight">{exam.code}</h1>
          <p className="text-[11px] text-slate-500 truncate">{exam.name}</p>
        </div>
      </div>

      {/* Prep cockpit: coverage ring + stats */}
      <FadeIn>
        <section
          aria-label="Your progress"
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-squishy-navy to-squishy-navySoft p-5 shadow-soft"
        >
          <div className="absolute -top-14 -right-14 w-44 h-44 rounded-full bg-squishy-teal/15 blur-2xl" aria-hidden />
          <div className="absolute -bottom-16 -left-10 w-40 h-40 rounded-full bg-squishy-purple/20 blur-2xl" aria-hidden />
          {coverage >= 100 && totalQ > 0 && <Confetti count={24} />}
          <div className="relative flex items-center gap-6">
            <div className="relative shrink-0" role="img" aria-label={`${coverage} percent of the question bank covered`}>
              <ProgressRing percent={coverage} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-display text-2xl font-semibold text-white leading-none">
                  <CountUp value={coverage} />%
                </span>
                <span className="text-[9px] font-semibold uppercase tracking-widest text-teal-200 mt-1">covered</span>
              </div>
            </div>
            <div className="flex-1 min-w-0 space-y-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-teal-200">Answered</div>
                <div className="font-display text-xl font-semibold text-white leading-tight">
                  <CountUp value={attempted} />
                  <span className="text-sm font-normal text-white/50"> of {totalQ.toLocaleString()}</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-teal-200">Accuracy</div>
                <div className={`font-display text-xl font-semibold leading-tight ${accuracy >= 70 ? 'text-squishy-teal' : accuracy >= 50 ? 'text-squishy-gold' : 'text-white'}`}>
                  <CountUp value={accuracy} />%
                </div>
              </div>
            </div>
          </div>
          <p className="relative mt-4 pt-3 border-t border-white/10 text-xs text-teal-100/90 font-medium">
            {heroLine}
          </p>
        </section>
      </FadeIn>

      {/* Start Studying */}
      {hasScienceVerbalSplit ? (
        <div className="grid grid-cols-2 gap-3">
          <Pressable
            haptic="medium"
            onClick={handleStartScience}
            disabled={feedLoading}
            className="h-14 rounded-2xl bg-gradient-to-br from-squishy-teal to-squishy-tealDark text-white font-semibold text-sm shadow-pop disabled:opacity-60"
          >
            {feedLoading ? 'Loading…' : 'Start Science'}
          </Pressable>
          <Pressable
            haptic="medium"
            onClick={handleStartVerbal}
            disabled={feedLoading}
            className="h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-squishy-purple text-white font-semibold text-sm shadow-pop disabled:opacity-60"
          >
            {feedLoading ? 'Loading…' : 'Start Verbal'}
          </Pressable>
        </div>
      ) : (
        <Pressable
          haptic="medium"
          onClick={handleStartScience}
          disabled={feedLoading}
          className="w-full h-14 rounded-2xl bg-gradient-to-br from-squishy-teal to-squishy-tealDark text-white font-semibold text-[15px] shadow-pop disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {feedLoading ? 'Loading…' : (
            <>
              Start Studying
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden>
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </>
          )}
        </Pressable>
      )}

      {/* Quick actions */}
      <nav aria-label="Exam sections" className="grid grid-cols-3 gap-2">
        <QuickAction
          to={`/exam/${examId}/topics`}
          label="Topics"
          tint="bg-teal-50 text-squishy-primary"
          icon={<path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />}
        />
        <QuickAction
          to={`/exam/${examId}/sessions`}
          label="Sessions"
          tint="bg-indigo-50 text-indigo-600"
          icon={<path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />}
        />
        <QuickAction
          to={`/exam/${examId}/leaderboard`}
          label="Ranks"
          tint="bg-amber-50 text-amber-600"
          icon={<path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />}
        />
      </nav>

      {/* Continue Studying (2 most recent + View All) */}
      {activeSessions.length > 0 && (
        <section className="space-y-2.5">
          <h2 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest px-1">Continue Studying</h2>
          <Stagger className="space-y-2.5">
            {previewSessions.map(session => (
              <Stagger.Item key={session.id}>
                <SessionCard
                  session={session}
                  sections={sections}
                  onResume={() => resumeSession(session)}
                  onDelete={() => setConfirmDeleteId(session.id)}
                />
              </Stagger.Item>
            ))}
          </Stagger>
          {activeSessions.length > 2 && (
            <Link
              to={`/exam/${examId}/sessions`}
              className="flex items-center justify-center gap-1.5 w-full min-h-[44px] py-3 rounded-2xl bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-squishy-primary transition-colors active:scale-[0.98]"
            >
              View All Sessions ({activeSessions.length})
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden>
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </Link>
          )}
        </section>
      )}

      {/* Marked for Review */}
      <MarkedForReviewCard
        allQuestions={allStudyableQuestions}
        createSession={createSession}
        navigate={navigate}
        sections={sections}
        scienceSections={scienceSections}
        contextType="exam"
      />

      {/* By Topic (first 2 + View All) */}
      {stats && stats.sections.length > 0 && (
        <section className="space-y-2.5">
          <h2 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest px-1">By Topic</h2>
          <Stagger className="space-y-2.5">
            {previewSections.map(s => (
              <Stagger.Item key={s.sectionId}>
                <SectionStatRow stat={s} onStart={() => handleStartSection(s.sectionId)} />
              </Stagger.Item>
            ))}
          </Stagger>
          {stats.sections.length > 2 && (
            <Link
              to={`/exam/${examId}/topics`}
              className="flex items-center justify-center gap-1.5 w-full min-h-[44px] py-3 rounded-2xl bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-squishy-primary transition-colors active:scale-[0.98]"
            >
              View All Topics ({stats.sections.length})
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden>
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </Link>
          )}
        </section>
      )}

      {/* Leaderboard (top 3 + View All) */}
      <section className="bg-white border border-slate-100 rounded-3xl shadow-card overflow-hidden">
        <div className="flex items-baseline justify-between px-5 pt-4 pb-2">
          <h2 className="font-display text-base font-semibold text-squishy-dark">Leaderboard</h2>
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">30 day</span>
        </div>
        <ol className="divide-y divide-slate-100">
          {previewLeaderboard.map(e => (
            <li key={e.userId} className={`flex items-center gap-3 px-5 py-3 ${e.isCurrentUser ? 'bg-teal-50' : ''}`}>
              <RankBadge rank={e.rank} />
              <div className="w-8 h-8 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-[10px] font-semibold text-slate-600 shrink-0">
                {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
              </div>
              <span className="flex-1 text-sm font-medium text-squishy-dark truncate inline-flex items-center gap-1 min-w-0">
                <span className="truncate">{e.displayName}</span>
                {e.verifiedStudent && <VerifiedBadge />}
              </span>
              <span className="text-xs font-semibold text-squishy-dark tabular-nums shrink-0">
                <CountUp value={e.xp} /> <span className="text-slate-400 font-medium">XP</span>
              </span>
            </li>
          ))}
          {(!lb?.entries || lb.entries.length === 0) && (
            <li className="px-5 py-8 text-center">
              <p className="text-sm font-medium text-slate-500">No activity yet.</p>
              <p className="text-xs text-slate-400 mt-1">Answer a few questions to claim the top spot.</p>
            </li>
          )}
        </ol>
        {lb?.entries && lb.entries.length > 3 && (
          <Link
            to={`/exam/${examId}/leaderboard`}
            className="flex items-center justify-center gap-1.5 w-full min-h-[44px] py-3 border-t border-slate-100 bg-slate-50 hover:bg-slate-100 text-sm font-semibold text-squishy-primary transition-colors"
          >
            View Full Leaderboard
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden>
              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
          </svg>
          </Link>
        )}
      </section>

      {/* ── Delete Session Confirmation ─────────────────────────── */}
      <Modal open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
        <div className="bg-white rounded-3xl p-6 shadow-pop text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center" aria-hidden>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="w-6 h-6 text-red-500">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </div>
          <div>
            <p className="font-display text-base font-semibold text-squishy-navy">Discard this session?</p>
            <p className="text-xs text-slate-500 mt-1">Your progress will be lost and can't be recovered.</p>
          </div>
          <div className="flex gap-2">
            <Pressable
              onClick={() => setConfirmDeleteId(null)}
              className="flex-1 h-12 rounded-2xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Keep
            </Pressable>
            <Pressable
              haptic="warning"
              onClick={() => { if (confirmDeleteId) deleteSession.mutate(confirmDeleteId); setConfirmDeleteId(null); }}
              className="flex-1 h-12 rounded-2xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
            >
              Discard
            </Pressable>
          </div>
        </div>
      </Modal>

      {/* Topic Picker Sheet */}
      <Modal open={showTopicPicker} onClose={() => setShowTopicPicker(false)} sheet="bottom">
        <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-pop max-h-[80vh] flex flex-col overflow-hidden">
          <div className="px-5 pt-5 pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-squishy-dark">Choose Topics</h2>
              <Pressable
                onClick={() => setShowTopicPicker(false)}
                aria-label="Close topic picker"
                className="w-11 h-11 -mr-2 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Pressable>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Select topics to focus on, or keep All selected.</p>
            <div className="mt-3 flex bg-slate-100 rounded-xl p-1 gap-1" role="group" aria-label="Question source">
              <Pressable
                onClick={() => { setQuestionFilter('all'); setSelectedTopics(new Set()); }}
                className={`flex-1 py-2.5 text-[11px] font-semibold rounded-lg transition-all ${
                  questionFilter === 'all' ? 'bg-white text-squishy-primary shadow-sm' : 'text-slate-500'
                }`}
              >
                All ({allStudyableQuestions.length})
              </Pressable>
              <Pressable
                onClick={() => { setQuestionFilter('mine'); setSelectedTopics(new Set()); }}
                className={`flex-1 py-2.5 text-[11px] font-semibold rounded-lg transition-all ${
                  questionFilter === 'mine' ? 'bg-white text-squishy-primary shadow-sm' : 'text-slate-500'
                }`}
              >
                My Q ({allStudyableQuestions.filter((q: any) => q.authorUserId === feedData?.currentUserId).length})
              </Pressable>
              <Pressable
                onClick={() => { setQuestionFilter('friends'); setSelectedTopics(new Set()); }}
                className={`flex-1 py-2.5 text-[11px] font-semibold rounded-lg transition-all ${
                  questionFilter === 'friends' ? 'bg-white text-squishy-primary shadow-sm' : 'text-slate-500'
                }`}
              >
                Friends Q ({allStudyableQuestions.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId)).length})
              </Pressable>
            </div>
          </div>

          {(() => {
            const passageCount = allStudyableQuestions.filter((q: any) => q.payload?.passage_type === 'passage-based' || q.payload?.passage).length;
            const discreteCount = allStudyableQuestions.length - passageCount;
            if (passageCount === 0) return null;
            return (
              <div className="px-5 py-2.5 border-b border-slate-100">
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-widest mb-1.5">Study Mode</p>
                <div className="flex bg-slate-100 rounded-xl p-1 gap-1" role="group" aria-label="Study mode">
                  <Pressable
                    onClick={() => { setStudyMode('all'); setSelectedTopics(new Set()); }}
                    className={`flex-1 py-2.5 text-xs font-semibold rounded-lg transition-all ${
                      studyMode === 'all' ? 'bg-white text-squishy-primary shadow-sm' : 'text-slate-500'
                    }`}
                  >All</Pressable>
                  <Pressable
                    onClick={() => { setStudyMode('passages'); setSelectedTopics(new Set()); }}
                    className={`flex-1 py-2.5 text-xs font-semibold rounded-lg transition-all ${
                      studyMode === 'passages' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'
                    }`}
                  >Passages ({passageCount})</Pressable>
                  <Pressable
                    onClick={() => { setStudyMode('discrete'); setSelectedTopics(new Set()); }}
                    className={`flex-1 py-2.5 text-xs font-semibold rounded-lg transition-all ${
                      studyMode === 'discrete' ? 'bg-white text-squishy-primary shadow-sm' : 'text-slate-500'
                    }`}
                  >Discrete ({discreteCount})</Pressable>
                </div>
              </div>
            );
          })()}

          {(targetSectionId || studyTarget === 'verbal' || studyTarget === 'science' || scienceSections.length === 1) && (
            <>
              {/* Unanswered Only toggle */}
              <div className="px-5 pt-3 pb-1 space-y-1">
                <label className="flex items-center justify-between cursor-pointer min-h-[44px]">
                  <div>
                    <span className="text-sm font-medium text-squishy-dark">Unanswered only</span>
                    <span className="ml-2 text-xs text-slate-400">
                      ({studyableQuestions.filter(q => !answeredSet.has(q.id)).length} new)
                    </span>
                  </div>
                  <div
                    onClick={() => { setUnansweredOnly(v => !v); setAnsweredWrongOnly(false); }}
                    role="switch"
                    aria-checked={unansweredOnly}
                    aria-label="Unanswered only"
                    className={`relative rounded-full transition-colors ${unansweredOnly ? 'bg-squishy-primary' : 'bg-slate-300'}`}
                    style={{ width: 46, height: 28 }}
                  >
                    <div
                      className="absolute top-[3px] left-[3px] bg-white rounded-full shadow transition-transform"
                      style={{ width: 22, height: 22, transform: unansweredOnly ? 'translateX(18px)' : 'translateX(0)' }}
                    />
                  </div>
                </label>
                {/* Answered Wrong Only toggle */}
                <label className="flex items-center justify-between cursor-pointer min-h-[44px]">
                  <div>
                    <span className="text-sm font-medium text-squishy-dark">Answered wrong only</span>
                    <span className="ml-2 text-xs text-slate-400">
                      ({wrongSet.size} to retry)
                    </span>
                  </div>
                  <div
                    onClick={() => { setAnsweredWrongOnly(v => !v); setUnansweredOnly(false); }}
                    role="switch"
                    aria-checked={answeredWrongOnly}
                    aria-label="Answered wrong only"
                    className={`relative rounded-full transition-colors ${answeredWrongOnly ? 'bg-red-500' : 'bg-slate-300'}`}
                    style={{ width: 46, height: 28 }}
                  >
                    <div
                      className="absolute top-[3px] left-[3px] bg-white rounded-full shadow transition-transform"
                      style={{ width: 22, height: 22, transform: answeredWrongOnly ? 'translateX(18px)' : 'translateX(0)' }}
                    />
                  </div>
                </label>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
                <Pressable
                  onClick={() => setSelectedTopics(new Set())}
                  className={`w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border-2 transition-colors ${
                    allSelected ? 'border-squishy-primary bg-teal-50 text-squishy-primary' : 'border-slate-200 text-slate-600'
                  }`}
                >
                  <span className="font-medium">All Topics</span>
                  <span className="text-xs text-slate-400">{applyFilters(studyableQuestions).length} questions</span>
                </Pressable>
                {availableTopics.map(t => {
                  const filteredCount = filteredTopicCounts.get(t.name) ?? t.count;
                  return (
                    <Pressable
                      key={t.name}
                      onClick={() => toggleTopic(t.name)}
                      aria-pressed={selectedTopics.has(t.name)}
                      className={`w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border-2 transition-colors ${
                        selectedTopics.has(t.name) ? 'border-squishy-primary bg-teal-50 text-squishy-primary' : 'border-slate-200 text-slate-600'
                      }`}
                    >
                      <span className="font-medium text-left">{t.name}</span>
                      <span className="text-xs text-slate-400 shrink-0 ml-2">{filteredCount}</span>
                    </Pressable>
                  );
                })}
              </div>
              <div className="px-5 py-4 border-t border-slate-100 space-y-3">
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest" htmlFor="session-name">Name this session</label>
                  <input
                    id="session-name"
                    type="text"
                    value={sessionName}
                    onChange={(e) => setSessionName(e.target.value.slice(0, 60))}
                    placeholder={examDefaultLabel || 'e.g. Bio passages'}
                    maxLength={60}
                    className="mt-1.5 w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-squishy-primary/30 focus:border-squishy-primary/50"
                  />
                </div>
                {/* How many questions this session (passage mode keeps full passages) */}
                {studyMode !== 'passages' && (
                  <div className="flex items-center justify-between min-h-[44px]">
                    <span className="text-sm font-medium text-squishy-dark">Questions this session</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        max={selectedQuestionCount}
                        value={Math.min(sessionCount, selectedQuestionCount) || ''}
                        onChange={(e) => setSessionCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                        aria-label="Number of questions"
                        className="w-16 border border-slate-200 rounded-xl px-2 py-2 text-center text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-squishy-primary/30"
                      />
                      <span className="text-xs text-slate-400">/ {selectedQuestionCount} max</span>
                      <Pressable
                        onClick={() => setSessionCount(selectedQuestionCount)}
                        className="text-xs font-semibold text-squishy-primary px-2 py-2 rounded-lg hover:bg-teal-50 transition-colors"
                      >
                        Max
                      </Pressable>
                    </div>
                  </div>
                )}
                <Pressable
                  haptic="medium"
                  onClick={confirmTopics}
                  disabled={createSession.isPending || selectedQuestionCount === 0}
                  className="w-full h-[52px] rounded-2xl bg-gradient-to-br from-squishy-teal to-squishy-tealDark text-white font-semibold text-base shadow-pop disabled:opacity-50 disabled:shadow-none transition-shadow"
                >
                  {createSession.isPending
                    ? 'Creating...'
                    : studyMode === 'passages'
                      ? `Start Studying${!allSelected ? ` · ${selectedQuestionCount} question${selectedQuestionCount !== 1 ? 's' : ''}` : ''}`
                      : `Start Studying · ${Math.min(Math.max(1, sessionCount), selectedQuestionCount)} question${Math.min(Math.max(1, sessionCount), selectedQuestionCount) !== 1 ? 's' : ''}`}
                </Pressable>
              </div>
            </>
          )}
        </div>
      </Modal>
    </PageTransition>
  );
}

// ── Progress Ring ───────────────────────────────────────────────────────────

function ProgressRing({ percent, size = 104, stroke = 9 }: { percent: number; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-white/10" />
      <motion.circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        strokeWidth={stroke}
        strokeLinecap="round"
        className="stroke-squishy-teal"
        strokeDasharray={c}
        initial={{ strokeDashoffset: c }}
        animate={{ strokeDashoffset: c * (1 - clamped / 100) }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
      />
    </svg>
  );
}

// ── Quick Action ────────────────────────────────────────────────────────────

function QuickAction({ to, label, icon, tint }: { to: string; label: string; icon: ReactNode; tint: string }) {
  return (
    <Link
      to={to}
      className="flex flex-col items-center justify-center gap-1.5 min-h-[72px] py-3 bg-white border border-slate-100 rounded-2xl shadow-card hover:shadow-pop transition-shadow active:scale-[0.97]"
    >
      <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${tint}`} aria-hidden>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor" className="w-5 h-5">
          {icon}
        </svg>
      </span>
      <span className="text-[11px] font-semibold text-slate-600">{label}</span>
    </Link>
  );
}

// ── Rank Badge ──────────────────────────────────────────────────────────────

function RankBadge({ rank }: { rank: number }) {
  const medal =
    rank === 1 ? 'bg-amber-50 text-amber-600'
    : rank === 2 ? 'bg-slate-100 text-slate-500'
    : rank === 3 ? 'bg-orange-50 text-orange-600'
    : 'bg-transparent text-slate-400';
  return (
    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold tabular-nums shrink-0 ${medal}`}>
      {rank}
    </span>
  );
}

// ── Session Card ────────────────────────────────────────────────────────────

function SessionCard({ session, sections, onResume, onDelete }: {
  session: StudySession;
  sections: any[];
  onResume: () => void;
  onDelete: () => void;
}) {
  const progress = session.totalQuestions > 0
    ? Math.round((session.currentIndex / session.totalQuestions) * 100)
    : 0;
  const remaining = session.totalQuestions - session.currentIndex;

  return (
    <div className="bg-white border border-slate-100 rounded-2xl pl-4 pr-2 py-2.5 shadow-card flex items-center gap-2">
      <div className="flex-1 min-w-0 cursor-pointer py-1" onClick={onResume}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-squishy-dark truncate">{session.label || 'All Topics'}</span>
          <span className="text-[10px] font-medium text-slate-400 shrink-0">{remaining} left</span>
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-squishy-teal to-emerald-400 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <span className="text-[10px] font-semibold text-slate-400 tabular-nums shrink-0 w-8 text-right">{progress}%</span>
        </div>
      </div>
      <Pressable
        onClick={onResume}
        className="bg-squishy-primary text-white px-4 h-11 rounded-xl text-xs font-semibold shrink-0 shadow-soft"
      >
        Resume
      </Pressable>
      <Pressable
        haptic="warning"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        aria-label="Discard session"
        className="w-10 h-11 flex items-center justify-center text-slate-300 hover:text-red-400 transition-colors shrink-0"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </Pressable>
    </div>
  );
}

// ── Marked for Review ───────────────────────────────────────────────────────

function MarkedForReviewCard({ allQuestions, createSession, navigate, sections, scienceSections, contextType }: {
  allQuestions: any[];
  createSession: ReturnType<typeof useCreateSession>;
  navigate: ReturnType<typeof useNavigate>;
  sections: any[];
  scienceSections?: any[];
  contextType: 'exam' | 'class';
}) {
  const flagged = getFlaggedQuestions();
  const questionIds = allQuestions.map(q => q.id);
  const flaggedInContext = flagged.filter(id => questionIds.includes(id));

  if (flaggedInContext.length === 0) return null;

  const handleStudyFlagged = async () => {
    const sectionId = contextType === 'exam'
      ? (scienceSections?.[0]?.id ?? sections[0]?.id)
      : sections[0]?.id;
    if (!sectionId || flaggedInContext.length === 0) return;

    const body: any = {
      questionIds: flaggedInContext,
      label: 'Marked for Review',
    };
    if (contextType === 'exam') {
      body.examSectionId = sectionId;
    } else {
      body.classSectionId = sectionId;
    }

    const result = await createSession.mutateAsync(body);
    const ctxType = contextType === 'exam' ? 'exam' : 'class';
    navigate(`/study/${ctxType}/${sectionId}?session=${result.session.id}`);
  };

  return (
    <Pressable
      onClick={handleStudyFlagged}
      disabled={createSession.isPending}
      className="w-full bg-white border border-amber-200 rounded-2xl p-4 shadow-card flex items-center gap-3 hover:bg-amber-50 transition-colors disabled:opacity-50"
    >
      <div className="w-11 h-11 rounded-2xl bg-amber-50 flex items-center justify-center shrink-0" aria-hidden>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-squishy-gold">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      </div>
      <div className="flex-1 text-left">
        <div className="text-sm font-semibold text-squishy-dark">Marked for Review</div>
        <div className="text-xs text-slate-500 mt-0.5">{flaggedInContext.length} question{flaggedInContext.length !== 1 ? 's' : ''} flagged</div>
      </div>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400 shrink-0" aria-hidden>
        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
      </svg>
    </Pressable>
  );
}

// ── Section Stat Row ────────────────────────────────────────────────────────

function SectionStatRow({ stat, onStart }: { stat: ExamSectionStat; onStart: () => void }) {
  const progress = stat.totalQuestions > 0 ? Math.round((stat.attempted / stat.totalQuestions) * 100) : 0;

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-card flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-sm text-squishy-dark truncate">{stat.sectionName}</span>
          <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide shrink-0">{stat.sectionCode}</span>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-squishy-teal to-emerald-400 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <span className="text-[10px] font-medium text-slate-500 tabular-nums shrink-0">{stat.attempted}/{stat.totalQuestions}</span>
        </div>
        <div className="text-[10px] text-slate-400 mt-1.5">
          <span className={`font-semibold ${stat.accuracy >= 70 ? 'text-emerald-600' : stat.accuracy >= 50 ? 'text-amber-600' : 'text-slate-500'}`}>{stat.accuracy}%</span> accuracy
        </div>
      </div>
      <Pressable
        onClick={onStart}
        className="bg-teal-50 text-squishy-primary px-4 h-11 rounded-xl text-xs font-semibold shrink-0"
      >
        Study
      </Pressable>
    </div>
  );
}
